import React, { FCWithChildren, useCallback, useMemo } from 'react';
import PersonalizedHeader from '@components/personalized/personalized-header/personalized-header';
import { useTranslation } from 'next-i18next';
import ProductFeedLayout from '@components/generic-product-feed/product-feed-layout/product-feed-layout';
import ProductFeed from '@components/generic-product-feed/product-feed/product-feed';
import useOnReveal from '@hooks/use-on-reveal';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { useRouter } from 'next/router';
import { CatalogProduct } from '@interfaces/models/catalogProduct';
import useProductFeedImpressionTracking from '@hooks/product-feed/use-product-feed-impression-tracking';

const ImpressionTrackingWrapper = ({ children, onRowReveal, index, productsOnRow }) => {
  const { containerRef } = useOnReveal(() => onRowReveal(index, productsOnRow), 1, false);
  return (
    <div
      className="impressionTrackingContainer"
      ref={containerRef}
    >
      {children}
    </div>
  );
};

const SimilarProductsList: React.FC<{
  similarProducts: CatalogProduct[];
  feedType: string;
  title: string;
  subtitle?: string;
  isFromFavTile?: boolean;
}> = ({ similarProducts, title, subtitle, feedType, isFromFavTile }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { sendAnalyticEvent: sendProductAnalyticEvent } = useAnalyticEvents('recommendations');

  const { onRowReveal, rowCount, hasWindowSizeBeenSet } = useProductFeedImpressionTracking(feedType);

  const handleExploreMoreCta = () => {
    sendProductAnalyticEvent('recommendations_empty_state');
    router.push(t('LINKS.NEW_IN'));
  };

  /*
   * TODO: create own wording for empty state on similar items instead of HERO_PDP.EMPTY_STATE
   */

  const similarProductsEmptyState = useMemo(() => {
    return {
      titleText: t('HERO_PDP.EMPTY_STATE.TITLE'),
      subTitleText: t('HERO_PDP.EMPTY_STATE.SUBTITLE'),
      clickHandler: handleExploreMoreCta,
      ctaText: t('HERO_PDP.SIMILAR_PRODUCT.DISCOVER_MORE'),
    };
  }, []);

  const recommendationEmptyState = useMemo(() => {
    return {
      titleText: t('RECOMMENDED_PRODUCT.EMPTY.TITLE.TEXT'),
      subTitleText: t('RECOMMENDED_PRODUCT.EMPTY.SUBTITLE_TEXT'),
      clickHandler: handleExploreMoreCta,
      ctaText: t('RECOMMENDED_PRODUCT.EMPTY.CTA_TITLE'),
    };
  }, []);

  const ProductCardWrapper: FCWithChildren<{ index: number }> = useCallback(
    ({ children, index }) =>
      hasWindowSizeBeenSet && index % rowCount === 0 ? (
        <ImpressionTrackingWrapper
          productsOnRow={similarProducts.slice(index, index + rowCount)}
          index={index}
          onRowReveal={onRowReveal}
        >
          {children}
        </ImpressionTrackingWrapper>
      ) : (
        <>{children}</>
      ),
    [onRowReveal, similarProducts, rowCount, hasWindowSizeBeenSet],
  );

  return (
    <ProductFeedLayout
      header={
        <PersonalizedHeader
          productsCount={isFromFavTile ? similarProducts?.length : 0}
          isLoading={false}
          title={title ? title : t('PRODUCT.DISCOVER.TITLE')}
          subTitle={subtitle ? subtitle : ''}
        />
      }
      productList={
        <ProductFeed
          products={similarProducts}
          pageType={isFromFavTile ? 'similar-products' : 'recommendations'}
          ProductCardWrapper={ProductCardWrapper}
          emptyState={isFromFavTile ? similarProductsEmptyState : recommendationEmptyState}
        />
      }
    />
  );
};

export default SimilarProductsList;
